import { Box, Button, Typography } from '@mui/material';
import Breadcrumbs from 'components/breadcrumbs';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import Paper from 'components/paper';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import _ from 'underscore';

import Pagination from 'components/pagination';
import { usePagination } from 'hooks/use-pagination';
import BluViuNutritionTabsHeader from 'views/bluviu-nutrition/components/bluviu-nutrition-tabs-header';
import SelectSite from 'views/bluviu-nutrition/components/select-site';
import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import { BluViuNutritionSite } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-site-type';
import { useSelectedSite } from 'views/bluviu-nutrition/hooks/use-selected-site';
import { BluViuNutritionParams } from 'views/bluviu-nutrition/types/bluviu-nutrition-params.type';

type Props = {};

const PAGINATION_LIMIT = 10;

const ListSites: React.FC<Props> = ({}) => {
  const history = useHistory();
  const params = useParams<BluViuNutritionParams>();
  const { currentPage, from, to, handlePagination } = usePagination(PAGINATION_LIMIT);

  const {
    r2ScenarioSitesQuery: { data: sites, isLoading },
  } = useR2ScenarioState();

  const [rows, setRows] = useState<BluViuNutritionSite[]>([]);

  useEffect(() => {
    if (!sites?.length) return;
    setRows(_.sortBy(sites, 'name'));
  }, [sites]);

  const handleClick = useCallback(
    (selectedSiteId: string) => {
      history.push(`/bluviu-nutrition/${params.tab}/${selectedSiteId}`);
    },
    [params.tab]
  );

  return (
    <Box display={'flex'} flexDirection={'column'}>
      <List
        isLoading={isLoading}
        noResult={{
          show: !sites?.length,
          text: 'No results',
        }}
        orderBy={'name'}
        rows={rows}
        sort={setRows}
        options={[
          { label: 'Site name', id: 'name' },
          { label: 'No. of scenarios', id: 'scenarioCount', align: 'right' },
          { label: 'No. of calibrations', id: 'calibrationCount', align: 'right' },
          { label: '', id: 'actions', align: 'right' },
        ]}>
        {rows?.slice(from, to).map((site, idx) => (
          <ListRow key={idx} onClick={() => handleClick(`${site.globalSiteId}`)}>
            <ListCell>{site.name}</ListCell>
            <ListCell align="right">{site.scenarioCount}</ListCell>
            <ListCell align="right">{site.calibrationCount}</ListCell>
            <ListCell align="right">
              <Button size="small" onClick={() => handleClick(site.globalSiteId)}>
                <FormattedMessage id={`general.view`} />
              </Button>
            </ListCell>
          </ListRow>
        ))}
      </List>
      <Pagination currentPage={currentPage} pages={Math.ceil(rows.length / PAGINATION_LIMIT)} onClick={handlePagination} />
    </Box>
  );
};

const SitesOverview: React.FC<Props> = ({}) => {
  const { selectedSiteId } = useSelectedSite();
  const params = useParams<BluViuNutritionParams>();
  const {
    r2ScenarioSitesQuery: { data: sites, isLoading },
  } = useR2ScenarioState();

  return (
    <>
      <BluViuNutritionTabsHeader
        intro={
          <Typography color="white">
            <FormattedMessage id={`general.confidential`} />
          </Typography>
        }
        utils={
          <Breadcrumbs
            crumbs={
              selectedSiteId
                ? [
                    {
                      text: sites?.find((s) => s.globalSiteId === params.globalSiteId)?.name || params.globalSiteId,
                      url: ``,
                    },
                  ]
                : undefined
            }
          />
        }
      />
      <Paper
        transparent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}>
        <Box display={'flex'} justifyContent={'flex-end'}>
          <SelectSite />
        </Box>
        <ListSites />
      </Paper>
    </>
  );
};

export default React.memo(SitesOverview);
